import { type ComponentProps, type ReactNode } from 'react';
import { cn } from '@ifixit/ui/lib/utils';

// Type definitions for better type safety
interface TableCell {
   id: string;
   label: ReactNode;
   type?: 'text' | 'image' | 'interactive';
   cellWidth?: string;
   cellStyles?: string;
}

interface TableRow {
   id: string | number;
   [key: string]: unknown;
}

interface TableProps<T extends TableRow = TableRow> {
   data: T[];
   cells: TableCell[];
   localize?: boolean;
   maxRows?: number;
   viewAllUrl?: string;
   viewAllText?: string;
}

// Constants for reusable class names
// max-w-[37.5vw] is used to cap the cell width and allow truncation (could use improvement)
const CELL_BASE_CLASSES =
   'px-3 md:px-4 py-[13.25px] text-left tabular-nums relative max-lg:max-w-[37.5vw]';

/**
 * Safely formats a cell value for display
 * Handles localization for numeric values with error handling
 */
function formatCellValue(value: unknown, shouldLocalize: boolean): ReactNode {
   if (value == null) {
      return '';
   }

   // If value is already a React element (like img tag), return it as is
   if (typeof value === 'object' && 'type' in value) {
      return value as ReactNode;
   }

   if (!shouldLocalize) {
      return String(value);
   }

   // Only localize if the value is a number
   if (typeof value === 'number') {
      return value.toLocaleString(undefined, { notation: 'compact' });
   }

   return String(value);
}

/**
 * Renders a table header row
 */
function TableHeader({ cells }: { cells: TableCell[] }) {
   return (
      <thead className="bg-gray-50">
         <tr>
            {cells.map(cell => (
               <th className={cn(CELL_BASE_CLASSES, cell.cellWidth, cell.cellStyles)} key={cell.id}>
                  <div className="truncate">{cell.label}</div>
               </th>
            ))}
         </tr>
      </thead>
   );
}

/**
 * Renders a single table row
 */
function TableBodyRow({
   row,
   cells,
   localize,
}: {
   row: TableRow;
   cells: TableCell[];
   localize: boolean;
}) {
   const ROW_LINK_CLASSES =
      'group [&_a]:no-underline [&_a]:transition-colors [&_a]:duration-150 transition-text-color duration-150';

   // Stretches a cell's anchor into a full-cell click target on row hover.
   // Cells typed `interactive` hold their own controls (multiple links or
   // buttons), so they opt out: stacked full-cell overlays would swallow
   // clicks for whichever anchor comes last in the DOM.
   const STRETCHED_LINK_CELL_CLASSES =
      "group-hover:[&_a]:text-brand-500 group-hover:[&_a]:before:content-[''] group-hover:[&_a]:before:cursor-inherit group-hover:[&_a]:before:block group-hover:[&_a]:before:absolute group-hover:[&_a]:before:inset-0 group-hover:[&_a]:before:z-0";

   return (
      <tr className={cn('border-t-1 border-gray-300', ROW_LINK_CLASSES)}>
         {cells.map(cell => (
            <td
               className={cn(
                  CELL_BASE_CLASSES,
                  cell.type !== 'interactive' && STRETCHED_LINK_CELL_CLASSES,
                  cell.cellStyles
               )}
               key={cell.id}
            >
               <div className="truncate">{formatCellValue(row[cell.id], localize)}</div>
            </td>
         ))}
      </tr>
   );
}

/**
 * Renders the "View All" link when there are more rows available
 */
function ViewAllLink({ url, text }: { url: string; text: string }) {
   return (
      <div className="mt-4">
         <a href={url} className="text-brand-500 hover:text-brand-600 hover:underline">
            {text}
         </a>
      </div>
   );
}

function Table<T extends TableRow = TableRow>({
   data,
   cells,
   localize = true,
   maxRows,
   viewAllUrl,
   viewAllText = 'View all',
}: TableProps<T> & Omit<ComponentProps<'div'>, keyof TableProps<T> | 'ref'>) {
   // Calculate if there are more rows than the maximum to display
   const hasMore = maxRows != null && data.length > maxRows;
   const displayData = maxRows == null ? data : data.slice(0, maxRows);

   return (
      <>
         <div className="border-1 border-gray-300 rounded-2xl overflow-hidden">
            <table className="w-full">
               <TableHeader cells={cells} />
               <tbody>
                  {displayData.map(row => (
                     <TableBodyRow key={row.id} row={row} cells={cells} localize={localize} />
                  ))}
               </tbody>
            </table>
         </div>
         {hasMore && viewAllUrl && <ViewAllLink url={viewAllUrl} text={viewAllText} />}
      </>
   );
}

export { Table };
export type { TableProps, TableCell, TableRow };
